import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, KeyRound, Landmark, Sun, Repeat, LogOut, ChevronDown, History } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useProfileModal } from '../context/ProfileModalContext';
import { useLumisial } from '../context/LumisialContext';
import { Badge } from './ui/Badge';
import { DROPDOWN_ANIMATION, DROPDOWN_BG } from './ui/tokens';
import { useDropdownAnimation } from '../hooks/useDropdownAnimation';
import { getUserPermissions, getZonalAccess } from '../utils/permissions';
import { ChangelogModal } from './changelog/ChangelogModal';
import { versionService, Version } from '../services/versionService';

export const UserProfileBadge: React.FC = () => {
  const { usuario, signOut } = useAuth();
  const { openProfile } = useProfileModal();
  const { selectedLumisial } = useLumisial();
  const navigate = useNavigate();
  const location = useLocation();
  const dropdown = useDropdownAnimation();
  const [showChangelog, setShowChangelog] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<Version | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const permissions = getUserPermissions(usuario);
  const zonalAccess = getZonalAccess(usuario);
  const isInAdminPanel = location.pathname.startsWith('/admin');
  const isInLumisialPanel = location.pathname.startsWith('/lumisial');

  const showAdminLink = permissions.canAccessAdmin && !isInAdminPanel;
  const showLumisialLink = !isInLumisialPanel;
  // Solo dentro de la vista de Lumisial (ahí es donde "cambiar" tiene sentido — en
  // Administración no hay lumisial activo) y solo con acceso a varios, la misma
  // condición que gateaba el ítem cuando vivía en el menú lateral (Layout.tsx, T6).
  const showChangeLumisial = isInLumisialPanel && zonalAccess.canAccessMultipleLumisiales;

  useEffect(() => {
    const loadCurrentVersion = async () => {
      try {
        const version = await versionService.getCurrentVersion();
        setCurrentVersion(version);
      } catch (error) {
        console.error('Error loading current version:', error);
      }
    };

    loadCurrentVersion();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        dropdown.close();
      }
    };

    if (dropdown.isMounted) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdown.isMounted, dropdown.close]);

  const getInitials = () => {
    if (!usuario) return '??';

    const firstNameInitial = usuario.nombres?.trim().split(' ')[0]?.[0] || '';
    const firstLastNameInitial = usuario.apellidos?.trim().split(' ')[0]?.[0] || '';

    return `${firstNameInitial}${firstLastNameInitial}`.toUpperCase();
  };

  const getDisplayName = () => {
    if (!usuario) return 'Usuario';

    const firstName = usuario.nombres?.trim().split(' ')[0] || '';
    return firstName;
  };

  const getFullDisplayName = () => {
    if (!usuario) return 'Usuario';

    const firstName = usuario.nombres?.trim().split(' ')[0] || '';
    const firstLastName = usuario.apellidos?.trim().split(' ')[0] || '';

    return `${firstName} ${firstLastName}`;
  };

  const handleViewProfile = () => {
    dropdown.close();
    openProfile('info');
  };

  const handleChangePassword = () => {
    dropdown.close();
    openProfile('security');
  };

  const handleOpenChangelog = () => {
    dropdown.close();
    setShowChangelog(true);
  };

  const handleGoToAdmin = () => {
    dropdown.close();
    navigate('/admin');
  };

  const handleGoToLumisial = () => {
    dropdown.close();
    if (selectedLumisial) {
      navigate('/lumisial/home');
    } else {
      navigate('/lumisial');
    }
  };

  // Sin anular la selección: igual que el botón del encabezado (Layout.tsx, T6), el
  // lumisial activo se conserva hasta que se elige otro en el selector.
  const handleChangeLumisial = () => {
    dropdown.close();
    navigate('/lumisial/cambiar');
  };

  const handleLogout = async () => {
    dropdown.close();
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const getRoleBadgeVariant = (roleName: string): 'info' | 'success' | 'default' => {
    if (roleName.includes('global')) return 'info';
    if (roleName.includes('admin')) return 'success';
    return 'default';
  };

  if (!usuario) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={dropdown.toggle}
        data-testid="user-menu-trigger"
        className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-gray-100 transition-colors active:scale-[0.98]"
      >
        <p className="text-sm font-medium text-gray-900">{getDisplayName()}</p>
        <div className="relative">
          {usuario.foto_url ? (
            <img
              src={usuario.foto_url}
              alt={getFullDisplayName()}
              className="w-9 h-9 rounded-full object-cover shadow-md"
            />
          ) : (
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-sm font-semibold shadow-md">
              {getInitials()}
            </div>
          )}
          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-200">
            <ChevronDown className={`w-2 h-2 text-gray-600 transition-transform ${dropdown.isOpen ? 'rotate-180' : ''}`} />
          </div>
        </div>
      </button>

      {dropdown.isMounted && (
        <div
          data-state={dropdown.dataState}
          className={`absolute right-0 mt-2 w-64 rounded-lg shadow-xl py-2 z-50 ${DROPDOWN_BG} ${DROPDOWN_ANIMATION}`}
          style={{ transformOrigin: 'top right' }}
          onAnimationEnd={dropdown.onAnimationEnd}
        >
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-3">
              {usuario.foto_url ? (
                <img
                  src={usuario.foto_url}
                  alt={getFullDisplayName()}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold">
                  {getInitials()}
                </div>
              )}
              <div className="flex flex-col items-start">
                <p className="font-medium text-gray-900">{getFullDisplayName()}</p>
                <p className="text-xs text-gray-600">{usuario.email}</p>
                <Badge variant={getRoleBadgeVariant(usuario.rol.nombre)} size="sm" className="mt-1">
                  {usuario.rol.nombre}
                </Badge>
              </div>
            </div>
          </div>

          <div className="py-1">
            <button
              onClick={handleViewProfile}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <User className="w-4 h-4" />
              <span>Mi Perfil</span>
            </button>

            <button
              onClick={handleChangePassword}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <KeyRound className="w-4 h-4" />
              <span>Cambiar Contraseña</span>
            </button>

            <button
              onClick={handleOpenChangelog}
              className="w-full flex items-center justify-between gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <History className="w-4 h-4" />
                <span>Historial de Versiones</span>
              </div>
              {currentVersion && (
                <span className="text-xs font-mono text-gray-500">
                  v{currentVersion.version_code}
                </span>
              )}
            </button>
          </div>

          {(showAdminLink || showLumisialLink || showChangeLumisial) && (
            <>
              <div className="border-t border-gray-100 my-1"></div>
              {showAdminLink && (
                <button
                  onClick={handleGoToAdmin}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Landmark className="w-4 h-4" />
                  <span>Panel de Administración</span>
                </button>
              )}
              {showLumisialLink && (
                <button
                  onClick={handleGoToLumisial}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Sun className="w-4 h-4" />
                  <span>Gestión de Lumisial</span>
                </button>
              )}
              {showChangeLumisial && (
                <button
                  type="button"
                  onClick={handleChangeLumisial}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Repeat className="w-4 h-4" />
                  <span>Cambiar Lumisial</span>
                </button>
              )}
            </>
          )}

          <div className="border-t border-gray-100 my-1"></div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span>Cerrar Sesión</span>
          </button>
        </div>
      )}

      <ChangelogModal
        isOpen={showChangelog}
        onClose={() => setShowChangelog(false)}
      />
    </div>
  );
};
